import type { ApiPaths } from '@reos/api-client';
import { apiClient } from './api.js';
import { unwrap } from './api-error.js';

/**
 * Krok L1 — datova vrstva sdruzene nabidky. Zadna logika: stavy parcel,
 * soucty vymer, cena i kontroly celku pocita SERVER (`@reos/duediligence`),
 * klient je zobrazuje. Duvod je stejny jako u mapovych vrstev (M1): dve
 * implementace tehoz pravidla se rozejdou.
 */

type OfferPath = '/v1/duediligence/cases/{id}/offer';
type LvSheetsPath = '/v1/duediligence/cases/{id}/lv-sheets';
type ParcelsPath = '/v1/duediligence/cases/{id}/offer/parcels';
type ParcelPath = '/v1/duediligence/cases/{id}/offer/parcels/{parcelId}';
type OwnerCheckPath = '/v1/duediligence/cases/{id}/offer/parcels/{parcelId}/owner-check';
type GraphicPath = '/v1/duediligence/cases/{id}/offer/graphic';
type ReportPath = '/v1/duediligence/cases/{id}/offer/report';
type GeometryPath = '/v1/duediligence/cases/{id}/offer/geometry.geojson';

export type OfferOverviewDto =
  ApiPaths[OfferPath]['get']['responses']['200']['content']['application/json'];
export type OfferParcelDto = OfferOverviewDto['parcels'][number];
export type OfferLvSheetDto = OfferOverviewDto['lvSheets'][number];
export type OfferFindingDto = OfferOverviewDto['wholeness']['findings'][number];
export type OfferSuggestionDto = OfferOverviewDto['suggestions'][number];
export type LvSheetResultDto =
  ApiPaths[LvSheetsPath]['post']['responses']['201']['content']['application/json'];
export type OwnerCheckResultDto =
  ApiPaths[OwnerCheckPath]['post']['responses']['200']['content']['application/json'];
export type OfferGraphicDto =
  ApiPaths[GraphicPath]['post']['responses']['201']['content']['application/json'];
export type OfferReportDto =
  ApiPaths[ReportPath]['get']['responses']['200']['content']['application/json'];
export type OfferGeometryDto =
  ApiPaths[GeometryPath]['get']['responses']['200']['content']['application/json'];

export type LvSheetSource = LvSheetResultDto['sheet']['source'];
export type OfferPreset = NonNullable<OfferGraphicDto['preset']>;

export function fetchOffer(caseId: string): Promise<OfferOverviewDto> {
  return unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/offer', { params: { path: { id: caseId } } }),
  );
}

/**
 * Vlozi list vlastnictvi. `text` je to, co makler zkopiroval z Nahlizeni do
 * KN — VCETNE jmen a adres vlastniku. Server z nich spocita otisk identity
 * a dal je zahodi; v odpovedi uz nejsou.
 */
export function submitLvSheet(
  caseId: string,
  input: { readonly source: LvSheetSource; readonly text: string; readonly paid?: boolean },
): Promise<LvSheetResultDto> {
  return unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/lv-sheets', {
      params: { path: { id: caseId } },
      body: {
        source: input.source,
        text: input.text,
        ...(input.paid === undefined ? {} : { paid: input.paid }),
      },
    }),
  ) as Promise<LvSheetResultDto>;
}

export function addParcelOutsideLv(
  caseId: string,
  input: { readonly ruianId?: string; readonly kuCode?: number; readonly label?: string },
): Promise<ApiPaths[ParcelsPath]['post']['responses']['201']['content']['application/json']> {
  return unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/offer/parcels', {
      params: { path: { id: caseId } },
      body: input,
    }),
  );
}

export function checkParcelOwner(
  caseId: string,
  parcelId: string,
  input: { readonly source: LvSheetSource; readonly text: string; readonly paid?: boolean },
): Promise<OwnerCheckResultDto> {
  return unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/offer/parcels/{parcelId}/owner-check', {
      params: { path: { id: caseId, parcelId } },
      body: {
        source: input.source,
        text: input.text,
        ...(input.paid === undefined ? {} : { paid: input.paid }),
      },
    }),
  ) as Promise<OwnerCheckResultDto>;
}

export function setParcelSelected(
  caseId: string,
  parcelId: string,
  selected: boolean,
): Promise<ApiPaths[ParcelPath]['patch']['responses']['200']['content']['application/json']> {
  return unwrap(
    apiClient.PATCH('/v1/duediligence/cases/{id}/offer/parcels/{parcelId}', {
      params: { path: { id: caseId, parcelId } },
      body: { selected },
    }),
  );
}

export async function removeParcel(caseId: string, parcelId: string): Promise<void> {
  await unwrap(
    apiClient.DELETE('/v1/duediligence/cases/{id}/offer/parcels/{parcelId}', {
      params: { path: { id: caseId, parcelId } },
    }),
  );
}

export function requestOfferGraphic(
  caseId: string,
  input: { readonly preset: OfferPreset; readonly archiveYear?: string },
): Promise<OfferGraphicDto> {
  return unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/offer/graphic', {
      params: { path: { id: caseId } },
      body: {
        preset: input.preset,
        ...(input.archiveYear === undefined ? {} : { archiveYear: input.archiveYear }),
      },
    }),
  ) as Promise<OfferGraphicDto>;
}

/** Parcely vyberu jako GeoJSON (5514) - mapa je vybarvi podle `state`. */
export function fetchOfferGeometry(caseId: string): Promise<OfferGeometryDto> {
  return unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/offer/geometry.geojson', {
      params: { path: { id: caseId } },
    }),
  );
}

export function fetchOfferReport(caseId: string): Promise<OfferReportDto> {
  return unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/offer/report', { params: { path: { id: caseId } } }),
  );
}
